import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Search, Calendar, Clock, ArrowRight, Loader2 } from "lucide-react";
import { format } from "date-fns";
import blogHeroImage from "@/assets/blog-hero-business.jpg";
import blogHeroVideo from "@/assets/blog-hero-business-cinematic.mp4.asset.json";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string | null;
  author: string;
  publish_date: string;
  reading_time: number | null;
  category_id: string | null;
  blog_categories: {
    name: string;
    slug: string;
  } | null;
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterPosts();
  }, [posts, searchQuery, selectedCategory]);

  const fetchPosts = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select(`
        *,
        blog_categories (name, slug)
      `)
      .eq("status", "published")
      .order("publish_date", { ascending: false });

    if (!error && data) {
      setPosts(data);
    }
    setIsLoading(false);
  };

  const fetchCategories = async () => {
    const { data } = await supabase
      .from("blog_categories")
      .select("*")
      .order("name");

    if (data) {
      setCategories(data);
    }
  };

  const filterPosts = () => {
    let filtered = posts;

    if (searchQuery) {
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter((post) => post.blog_categories?.slug === selectedCategory);
    }

    setFilteredPosts(filtered);
  };

  return (
    <>
      <Helmet>
        <title>Blog | Rick's Black Car Service - San Antonio Transportation Tips & Guides</title>
        <meta
          name="description"
          content="Read expert tips, local guides, and insights about luxury transportation in San Antonio. Airport transfers, corporate travel, special events, and more."
        />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/blog" />
        <meta property="og:title" content="Blog | Rick's Black Car Service" />
        <meta property="og:description" content="Expert tips and guides for luxury transportation in San Antonio" />
        <meta property="og:url" content="https://ricksblackcarservicesantonio.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-24 overflow-hidden">
          {/* Cinematic Background Video */}
          <div className="absolute inset-0 overflow-hidden">
            <video
              src={blogHeroVideo.url}
              poster={blogHeroImage}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover object-[center_25%]"
             disableRemotePlayback/>
            <div className="absolute inset-0 bg-gradient-to-br from-background/75 via-background/65 to-background/80" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,hsl(var(--background)/0.55)_100%)]" />
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-4 relative z-10 pt-8">
            <p className="text-xl md:text-2xl lg:text-3xl font-bold text-primary mb-4 text-center tracking-wider uppercase animate-[linq-glow_3s_ease-in-out_infinite]" style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 215, 0, 0.6), 0 0 40px rgba(255, 215, 0, 0.4)' }}>
              Our Blog
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 text-center drop-shadow-lg">
              Transportation Insights
            </h1>
            <p className="text-lg md:text-xl text-foreground/90 max-w-3xl mx-auto text-center drop-shadow-md">
              Expert tips, local guides, and stories from San Antonio's premier black car service
            </p>
          </div>
        </section>

        {/* Search and Filters */}
        <section className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(null)}
              >
                All Articles
              </Button>
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.slug ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category.slug)}
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="container mx-auto px-4 pb-20">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="flex flex-col hover:shadow-lg transition-shadow">
                  {post.featured_image && (
                    <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    {post.blog_categories && (
                      <Badge variant="secondary" className="w-fit mb-2">
                        {post.blog_categories.name}
                      </Badge>
                    )}
                    <CardTitle className="line-clamp-2">
                      <Link to={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                        {post.title}
                      </Link>
                    </CardTitle>
                    <CardDescription className="line-clamp-3">{post.excerpt}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(post.publish_date), "MMM d, yyyy")}
                      </div>
                      {post.reading_time && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {post.reading_time} min read
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Link to={`/blog/${post.slug}`} className="w-full">
                      <Button variant="outline" className="w-full group">
                        Read More
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
